import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { SafeResourceUrl } from "@angular/platform-browser";
import { NzModalService } from "ng-zorro-antd/modal";
import { Subscription } from "rxjs";
import { AppUtils, Constants } from "src/helpers";
import { ListenerService } from "src/services";
import { SharedModule } from '../../../shared/shared.module';
import { VideoViewerComponent } from "../video-viewer/video-viewer.component";
import { PdfViewerComponent } from "../pdf-viewer/pdf-viewer.component";
import { DisplayLivePreviewComponent } from "src/app/display/components/live-preview/display-live-preview.component";

@Component({
    standalone: true,
    imports: [SharedModule, VideoViewerComponent, PdfViewerComponent],
    selector: 'app-display-preview',
    templateUrl: './display.preview.component.html',
    styleUrls: ['./display.preview.component.scss']
})
export class DisplayPreviewComponent implements OnInit, OnChanges {
    mediaType = Constants.mediaAppType;
    @Input() type: number;
    @Input() imageUrl: string | null;
    @Input() noMediaInPlaylist: boolean;
    @Input() hidePreviewMessage: boolean;
    @Input() lastUpdated: string;
    @Input() uploadedMediaCount: number;
    @Input() uploadMedia: boolean;
    @Input() uploadMediaFromBroadcast: boolean;
    @Input() uploadMediaFromPlaylist: boolean;
    @Input() mediaExists: string;
    @Input() screenOrientation: number;
    @Input() pageChangeDuration: number;
    /** Live iframe preview (e.g. a playing playlist) shown bezel-less instead of the static screenshot/icon. */
    @Input() previewIframeUrl: SafeResourceUrl | null = null;
    @Input() isPreviewIframeLoading: boolean;
    /** NZ_MODAL_DATA payload for DisplayLivePreviewComponent (e.g. { displayListId }); clicking the live preview opens it enlarged when set. */
    @Input() previewModalData: any = null;
    @Output() onError: EventEmitter<any> = new EventEmitter();
    @Output() iframeLoad: EventEmitter<void> = new EventEmitter();
    @Output() iframeError: EventEmitter<void> = new EventEmitter();
    showDefault = true;
    mediaUploaded: boolean;
    noPlaylistOrBroadcast: boolean;
    noMediaInAddedPlaylist: boolean;
    lastUpdatedAt: string;
    url: string;
    isTypeIconFallback: boolean;
    subscriptions = new Array<Subscription>();

    constructor(private appUtils: AppUtils,
        private listenerService: ListenerService,
        private modal: NzModalService
    ) {
        const appThemeChange = this.listenerService
            .appThemeChange.subscribe(() => {
                this.handlePreview();
            });
        this.subscriptions.push(appThemeChange);
    }

    ngOnInit() {
        this.handlePreview()
    }

    ngOnChanges() {
        this.handlePreview();
    }

    isVideoPreview(): boolean {
        return [this.mediaType.youTube, this.mediaType.video, this.mediaType.vimeo].includes(this.type)
            || this.isVideoUrl(this.url ?? this.imageUrl);
    }

    isPdfPreview(): boolean {
        return this.type === this.mediaType.pdf;
    }

    isImagePreview(): boolean {
        return !this.isVideoPreview() && !this.isPdfPreview();
    }

    private isVideoUrl(url: string | null | undefined): boolean {
        if (!url || typeof url !== 'string') {
            return false;
        }

        const normalized = url.toLowerCase();
        return normalized.includes('.m3u8')
            || normalized.includes('video-hls/')
            || normalized.includes('video-media/')
            || /\.(mp4|mov|mkv|webm)(\?|$)/.test(normalized);
    }

    handlePreview(): void {
        const hasImageUrl = typeof this.imageUrl === 'string'
            ? this.imageUrl.trim().length > 0
            : !!this.imageUrl;

        if (hasImageUrl) {
            this.url = this.imageUrl;
            this.showDefault = false;
        }
        else {
            this.showDefault = true;
            if (this.appUtils.isDarkModeActive()) {
                this.url = "./assets/images/darkmode-preview.svg";
            }
            else {
                this.url = "./assets/images/default_preview.png";
            }
        }

        // Media-type logo fallback (no live screenshot available) is a small icon, not a
        // screen capture — it must not be stretched to fill the TV-frame preview container.
        this.isTypeIconFallback = typeof this.url === 'string' && this.url.includes('/media-types/preview/');

        if (this.noMediaInPlaylist) {
            this.noMediaInAddedPlaylist = true;
            this.noPlaylistOrBroadcast = false;
        }
        else {
            this.noPlaylistOrBroadcast = true;
            this.noMediaInAddedPlaylist = false;
        }

        if (this.lastUpdated) {
            this.lastUpdatedAt = this.lastUpdated;
        }

        if (this.uploadedMediaCount > 0) {
            this.mediaUploaded = true;
        }
        else {
            this.mediaUploaded = false;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => { s.unsubscribe(); });
    }
    open(url: string) {
        if(this.type){
            window.open(url, '_blank');
        }
    }

    openPreviewModal(): void {
        if (!this.previewModalData) {
            return;
        }
        this.modal.create({
            nzContent: DisplayLivePreviewComponent,
            nzData: this.previewModalData,
            nzWidth: '80vw',
            nzFooter: null,
        });
    }
}
